import { useState } from "react";
import { reportLabel } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { CheckCircle2, Clock, AlertTriangle, Circle } from "lucide-react";

const STATUS_ICON = {
  pending: Circle,
  in_progress: Clock,
  complete: CheckCircle2,
  flagged: AlertTriangle,
  not_requested: Circle,
};

const STATUS_COLOR = {
  pending: "text-[#4A5568]",
  in_progress: "text-[#F59E0B]",
  complete: "text-[#22C55E]",
  flagged: "text-[#EF4444]",
  not_requested: "text-[#0A192F]/20",
};

export default function ReportRow({ report, isStaff, onUpdate }) {
  const Icon = STATUS_ICON[report.status] || Circle;
  const [editing, setEditing] = useState(false);
  const [status, setStatus] = useState(report.status);
  const [summary, setSummary] = useState(report.result_summary || "");

  if (!report.requested) {
    return (
      <div className="flex items-center gap-4 p-4 opacity-40">
        <Circle className="h-5 w-5 text-[#0A192F]/20" />
        <div className="flex-1"><div className="text-sm text-[#4A5568]">{reportLabel(report.type)}</div></div>
        <span className="font-mono text-[10px] text-[#4A5568]">NOT REQUESTED</span>
      </div>
    );
  }

  return (
    <div className="p-4 border-b border-[#0A192F]/10 last:border-0" data-testid={`report-${report.type}`}>
      <div className="flex items-center gap-4">
        <Icon className={`h-5 w-5 ${STATUS_COLOR[report.status]}`} />
        <div className="flex-1">
          <div className="font-semibold text-[#0A192F]">{reportLabel(report.type)}</div>
          {report.result_summary && <div className="text-xs text-[#4A5568] mt-1">{report.result_summary}</div>}
        </div>
        <span className={`font-mono text-[10px] tracking-[0.1em] uppercase ${STATUS_COLOR[report.status]}`}>{report.status?.replace("_", " ")}</span>
        {isStaff && !editing && (
          <Button variant="outline" size="sm" className="rounded-sm" onClick={() => setEditing(true)} data-testid={`edit-${report.type}`}>Update</Button>
        )}
      </div>
      {editing && (
        <div className="mt-4 space-y-3 pl-9" data-testid={`edit-form-${report.type}`}>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="rounded-sm h-9" data-testid={`status-select-${report.type}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="in_progress">In Progress</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
              <SelectItem value="flagged">Flagged</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Result summary (optional)" rows={2} value={summary} onChange={(e) => setSummary(e.target.value)} className="rounded-sm" data-testid={`summary-${report.type}`} />
          <div className="flex gap-2">
            <Button size="sm" className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm" onClick={async () => { await onUpdate(report.type, status, summary); setEditing(false); }} data-testid={`save-${report.type}`}>Save</Button>
            <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(false)}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}
